import {
  CopyTextBox,
  DangerSecondaryButton,
  PrimaryButton,
  SecondaryButton,
  TextField,
} from "@graffer-inc/console-design-kit";
import classNames from "classnames";
import React, { useState } from "react";
import ReactDatePicker from "react-datepicker";
import { matchType } from "../../../../../application/domain/base/base-type";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import { CustomPath } from "@graffer-inc/smart-apply";
import { formatDatetime } from "../../../../../utils/date-utils";
import { PORTAL_BASE_URL } from "../../../../../utils/url-utils";
import { BaseModal } from "../../../../atoms/base-modal";
import { ErrorMessage } from "../../../../atoms/error-message";
import {
  useAssignCustomPath,
  validatePath,
  validateStartDatetime,
} from "./use-assign-custom-path";
import { RequiredTag } from "../../../../atoms/required-tag";

export type AssignCustomPathModalContainerProps = {
  onClose: () => void;
  orgSlug: string;
  procedure: ProcedureEntity;
  afterReallocate: (procedureId: string) => Promise<void>;
};

export const AssignCustomPathModalContainer: React.FC<
  AssignCustomPathModalContainerProps
> = ({ onClose, orgSlug, procedure, afterReallocate }) => {
  const props = useAssignCustomPath(
    procedure.orgId,
    procedure.id,
    afterReallocate,
  );
  return (
    <AssignCustomPathModal
      onClose={onClose}
      orgSlug={orgSlug}
      procedureName={procedure.name}
      {...props}
    />
  );
};

export type AssignCustomPathModalProps = {
  onClose: () => void;
  orgSlug: string;
  procedureName: string;
  onReallocateCustomPath: (
    newCustomPath: string,
    startDatetime?: Date,
  ) => Promise<void>;
  onDeleteReservedCustomPath: () => void;
  currentPaths: {
    current?: CustomPath;
    next?: CustomPath;
  };
  pageStatus: "EDIT" | "CONFIRM";
  newPath: string;
  newStartDatetime?: Date;
  isLoading: boolean;
  onChangePath: (path: string) => void;
  onChangeStartDate: (startDate?: Date) => void;
  onConfirm: () => Promise<void>;
  onBackToEdit: () => void;
  usingPathProcName?: string;
  canAssign: boolean | undefined;
  reservedPathProcName: string | undefined;
  isReserved: boolean;
};

export const AssignCustomPathModal: React.FC<AssignCustomPathModalProps> = ({
  onClose,
  currentPaths,
  pageStatus,
  orgSlug,
  procedureName,
  newPath,
  newStartDatetime,
  usingPathProcName,
  onChangePath,
  onChangeStartDate,
  onConfirm,
  onBackToEdit,
  onReallocateCustomPath,
  onDeleteReservedCustomPath,
  isLoading,
  canAssign,
  reservedPathProcName,
  isReserved,
}) => {
  const [pathTouched, setPathTouched] = useState(false);
  const [dateTouched, setDateTouched] = useState(false);

  return (
    <BaseModal
      title={matchType(pageStatus, {
        EDIT: "申請開始ページURLを予約",
        CONFIRM: "申請開始ページURLの予約を確認",
      })}
      isActive
      onClose={onClose}
      style={{ width: "700px" }}
    >
      {matchType(pageStatus, {
        EDIT: (
          <>
            <section className="modal-card-body">
              <>
                {(currentPaths.current || currentPaths.next) && (
                  <>
                    <label htmlFor="name" className="label">
                      現在の設定
                    </label>
                  </>
                )}
                {currentPaths.current && (
                  <div className="field">
                    <p className="help has-text-weight-normal">
                      以下のURLから申請開始ページを確認することができます。
                    </p>
                    <div className="control">
                      <div className="columns">
                        <div className="column">
                          <CopyTextBox
                            value={`${PORTAL_BASE_URL}/${orgSlug}/smart-apply/apply-procedure-alias/${currentPaths.current.customPath}`}
                          />
                        </div>
                        {currentPaths.next && (
                          // 予約されたカスタムパスの削除ボタンがある場合は、現在有効なカスタムパスのスペースを統一する
                          <div className="column is-2"></div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {currentPaths.next && (
                  <>
                    <div className="help has-text-weight-normal">
                      <strong>
                        {formatDatetime(currentPaths.next.startUnixtime * 1000)}
                      </strong>{" "}
                      以降、以下のURLから申請開始ページを確認することができます。
                    </div>
                    <div className="field">
                      <div className="control">
                        <div className="columns">
                          <div className="column">
                            <CopyTextBox
                              value={`${PORTAL_BASE_URL}/${orgSlug}/smart-apply/apply-procedure-alias/${currentPaths.next.customPath}`}
                            />
                          </div>
                          <div className="column is-2 pl-0">
                            <DangerSecondaryButton
                              text={"取り消す"}
                              onClick={() => {
                                onDeleteReservedCustomPath();
                                // 削除時には、パスと反映時刻の入力欄が空になる。
                                // それらは必須項目のため、本来空だとバリデーションエラーになるが、削除時に空になるのはやむを得ない。
                                // そのため、バリデーションエラーを表示しないようにする。
                                setPathTouched(false);
                                setDateTouched(false);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
              <div className="field">
                <TextField
                  label={"予約する申請開始ページURL"}
                  type={"url"}
                  description={
                    <p
                      className="help has-text-weight-normal is-inline-block"
                      style={{ lineHeight: 1.5 }}
                    >
                      指定した反映日時に、申請開始ページのURLの末尾を変更できます。
                      <br />
                      文字列には「半角英数字」「ハイフン（-）」が使用できます。
                    </p>
                  }
                  placeholder={"move-out"}
                  onBlur={() => setPathTouched(true)}
                  onChange={(e) => onChangePath(e.target.value)}
                  errorMessages={
                    pathTouched
                      ? validatePath(newPath, currentPaths.current?.customPath)
                      : undefined
                  }
                  required
                  value={newPath}
                />
                <div
                  className="ml-2 mt-2"
                  style={{ overflowWrap: "break-word" }}
                >
                  <span className="has-text-grey">
                    {PORTAL_BASE_URL}/{orgSlug}
                    /smart-apply/apply-procedure-alias/
                  </span>
                  <b>{newPath}</b>
                </div>
              </div>
              <div className="field">
                <div className="columns mx-0 mt-0 mb-3">
                  <div className="column is-narrow p-0">
                    <label htmlFor="reflect-date" className="label">
                      反映日時
                    </label>
                  </div>
                  <div className="column is-narrow p-0">
                    <RequiredTag />
                  </div>
                </div>
                <ReactDatePicker
                  className={classNames("input", {
                    "is-danger": dateTouched
                      ? validateStartDatetime(newStartDatetime)
                      : undefined,
                  })}
                  id="reflect-date"
                  selected={newStartDatetime}
                  onChange={(date: Date) => onChangeStartDate(date)}
                  onBlur={() => setDateTouched(true)}
                  placeholderText="----/--/-- --:--"
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  timeCaption="時分"
                  dateFormat="yyyy/MM/dd HH:mm"
                />
                <ErrorMessage
                  msg={
                    dateTouched
                      ? validateStartDatetime(newStartDatetime)
                      : undefined
                  }
                />
              </div>
            </section>
            <footer className="modal-card-foot is-justify-content-center">
              <SecondaryButton text={"キャンセル"} onClick={onClose} />
              <PrimaryButton
                text={"設定を確認する"}
                disabled={
                  !!validatePath(newPath, currentPaths.current?.customPath) ||
                  !!validateStartDatetime(newStartDatetime)
                }
                onClick={onConfirm}
              />
            </footer>
          </>
        ),
        CONFIRM: (
          <>
            <section className="modal-card-body">
              {!canAssign ? (
                <p className="notification is-warning is-light">
                  入力されたURLは、すでに他のグループによって管理されている手続きに使用されているため、予約することができません。
                </p>
              ) : isReserved ? (
                <p className="notification is-warning is-light">
                  入力されたURLは、現在「{reservedPathProcName}
                  」で予約されています。
                  <br />
                  既に予約されているURLを予約することはできません。
                </p>
              ) : (
                <>
                  <div>
                    {currentPaths.current && (
                      <>
                        <div className="field">
                          <label htmlFor="name" className="label">
                            変更前
                          </label>
                          <div className="control">
                            <span className="tag is-medium">
                              {`${PORTAL_BASE_URL}/${orgSlug}/smart-apply/apply-procedure-alias/${currentPaths.current.customPath}`}
                            </span>
                          </div>
                        </div>
                        <div className="spacer" />
                      </>
                    )}
                    <div className="field">
                      <label htmlFor="name" className="label">
                        {currentPaths.current ? "変更" : "設定"}後
                        <p className="help has-text-weight-normal">
                          この内容で更新すると、
                          {newStartDatetime && "反映日時以降は"}
                          以下のURLから申請ページにアクセスできるようになります。
                        </p>
                      </label>
                      <div
                        className={classNames("control", {
                          "is-loading": isLoading,
                        })}
                      >
                        <span className="tag is-medium">{`${PORTAL_BASE_URL}/${orgSlug}/smart-apply/apply-procedure-alias/${newPath}`}</span>
                      </div>
                    </div>
                    {!!usingPathProcName && (
                      <div className="field">
                        <p className="notification is-warning is-light">
                          入力されたURLは、現在「{usingPathProcName}
                          」で使用されています。
                          <br />
                          {formatDatetime(newStartDatetime)}
                          に、「
                          {usingPathProcName}
                          」のURL設定が解除され「{procedureName}
                          」に割り当てられます。
                        </p>
                      </div>
                    )}
                    {!!newStartDatetime && (
                      <div className="field">
                        <div className="label">反映日時</div>
                        <div className="control">
                          <span className="tag is-medium">
                            {formatDatetime(newStartDatetime)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>
            <footer className="modal-card-foot is-justify-content-center">
              <SecondaryButton text={"設定に戻る"} onClick={onBackToEdit} />
              <PrimaryButton
                text={"この内容で予約する"}
                disabled={!canAssign || isReserved || isLoading}
                onClick={async () => {
                  onReallocateCustomPath(newPath, newStartDatetime);
                  onClose();
                }}
              />
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};
